import {
  Component,
  inject,
  signal,
  computed,
  effect,
  untracked,
  ChangeDetectionStrategy,
} from "@angular/core";
import { RouterLink } from "@angular/router";
import { DetailedUserInterface } from "src/app/core/interfaces/detailed-user-interface";
import { DetailedUserService } from "src/app/features/services/user-profile/detailed-user-service";
import { ContactCard } from "src/app/shared/components/contact-card/contact-card";
import { Loader } from "src/app/shared/components/loader/loader";
import { Tile } from "src/app/shared/components/tile/tile";
import { Button } from "src/app/shared/components/utils/button/button";
import { Icon } from "src/app/shared/components/utils/icon/icon";
import { MobilityRefService } from "../../services/mobility/mobility-ref-service";
import { TranslocoModule } from "@jsverse/transloco";
import { AuthService } from "src/app/core/services/auth-service";
import {
  FavoriteFilterSearchInterface,
  TileFavoriteInterface,
} from "src/app/core/interfaces/favorite-interface";
import { CardFavorite } from "src/app/shared/components/card-favorite/card-favorite";
import { CardFavoriteSearch } from "src/app/shared/components/card-favorite-search/card-favorite-search";
import { FavoriteStore } from "../../store/favorite-store";
import { TileTypeEnum } from "src/app/core/interfaces/tile-interface";
import { FavoriteSearchApplierService } from "../../services/favorite-search-applier.service";
import { FavoritesService } from "../../services/favorites/favorites-service";
import { ErrorModalService } from "src/app/core/services/error-modal-service";
import { LanguageService } from "src/app/core/services/language-service";

@Component({
  selector: "app-user-profile",

  imports: [
    ContactCard,
    RouterLink,
    Icon,
    Button,
    Tile,
    Loader,
    TranslocoModule,
    CardFavorite,
    CardFavoriteSearch,
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  templateUrl: "./user-profile.html",
})
export class UserProfileComponent {
  private readonly languageService = inject(LanguageService);

  private readonly _detailedUserService = inject(DetailedUserService);
  private readonly _mobilityRefService = inject(MobilityRefService);
  private readonly _favoriteService = inject(FavoritesService);
  private readonly authService = inject(AuthService);
  private readonly favoriteStore = inject(FavoriteStore);
  private readonly favoriteSearchApplier = inject(FavoriteSearchApplierService);
  private readonly errorModalService = inject(ErrorModalService);

  public userProfile?: DetailedUserInterface;

  loadingDashboard = signal<boolean>(false);
  loadingFavoriteContacts = signal<boolean>(false);
  loadingFavoriteTiles = signal<boolean>(false);
  loadingFavoriteSearches = signal<boolean>(false);
  showFavoriteContactsForMobile = signal<boolean>(false);
  isInEditMode = signal<boolean>(false);
  contactsToRemoveFromFavorite: string[] = [];
  isInEditFavoritesMode = signal<boolean>(false);
  tilesToRemoveFromFavorites = signal<string[]>([]);
  searchesToRemoveFromFavorite = signal<number[]>([]);

  tilesFavorites = computed(() => {
    const allFavorites = this.favoriteStore.favoriteTiles();
    const filtered = allFavorites.filter((t) => {
      return t.tileType === TileTypeEnum.Other;
    });
    return filtered.map(
      (t) =>
        ({
          id: t.id,
          title: t.title,
          link: t.link,
          textDisclaimer: t.textDisclaimer,
          isExternalLink: t.isExternalLink,
        }) as TileFavoriteInterface
    );
  });

  videoFavorites = computed(() => {
    const allFavoriteTiles = this.favoriteStore.favoriteTiles();
    const filtered = allFavoriteTiles.filter((t) => {
      return t.tileType === TileTypeEnum.Video;
    });
    return filtered.map(
      (t) =>
        ({
          id: t.id,
          title: t.title,
          link: t.link,
          textDisclaimer: t.textDisclaimer,
          isExternalLink: t.isExternalLink,
        }) as TileFavoriteInterface
    );
  });

  searchFavorites = computed(() => {
    return this.favoriteStore.favoriteSearches();
  });

  constructor() {
    effect(() => {
      this.languageService.currentLanguage();

      untracked(async () => {
        this.loadingDashboard.set(true);
        this.favoriteStore.setFavoriteSearchLoading(true);
        this.loadingFavoriteSearches.set(true);
        this.loadingFavoriteTiles.set(true);
        if (this.favoriteStore.favoriteTiles().length === 0) {
          this.favoriteStore.setLoading(true);
        }
        this.loadingFavoriteContacts.set(true);

        // Load Dashboard
        try {
          this.userProfile = await this._detailedUserService.getDetailedUser();
        } catch (error) {
          this.loadingFavoriteContacts.set(false);
          if (this.errorModalService.shouldOpenErrorModal(error)) {
            this.errorModalService.openErrorModal();
          }
        } finally {
          this.loadingDashboard.set(false);
        }

        await Promise.all([
          this.loadFavoriteContacts(),
          this.loadFavoritesTilesAndSearches(),
        ]);
      });
    });
  }

  private async loadFavoriteContacts(): Promise<void> {
    try {
      if (this.userProfile) {
        this.userProfile.contacts =
          await this._favoriteService.getFavoriteContacts();
      }
    } finally {
      this.loadingFavoriteContacts.set(false);
    }
  }

  private async loadFavoritesTilesAndSearches(): Promise<void> {
    try {
      await this.favoriteStore.loadFavoriteSearches();
      if (this.favoriteStore.favoriteTiles().length === 0) {
        await this.favoriteStore.loadFavoriteTiles();
      }
    } finally {
      this.loadingFavoriteSearches.set(false);
      this.loadingFavoriteTiles.set(false);
    }
  }

  setShowFavoriteContactsForMobile(value: boolean) {
    this.showFavoriteContactsForMobile.set(value);
    if (value === false) {
      this.isInEditMode.set(false);
      this.contactsToRemoveFromFavorite = [];
    }
  }

  editFavoriteContacts() {
    this.isInEditMode.set(true);
  }

  closeEditFavoriteContacts() {
    this.contactsToRemoveFromFavorite = [];
    this.isInEditMode.set(false);
  }

  onDeletedTicked(spId: number) {
    if (this.contactsToRemoveFromFavorite.includes(spId.toString())) {
      this.contactsToRemoveFromFavorite =
        this.contactsToRemoveFromFavorite.filter((s) => s != spId.toString());
    } else {
      this.contactsToRemoveFromFavorite.push(spId.toString());
    }
  }

  async saveFavoriteContacts() {
    await this._mobilityRefService.deleteManyMobilityRefsFavorite(
      this.contactsToRemoveFromFavorite
    );
    this.userProfile.contacts = this.userProfile.contacts.filter(
      (s) =>
        !this.contactsToRemoveFromFavorite.some((d) => d === s.spId.toString())
    );
    this.contactsToRemoveFromFavorite = [];
    this.isInEditMode.set(false);
  }

  async disconnect(): Promise<void> {
    await this.authService.logout();
  }

  toggleEditFavoriteMode() {
    this.isInEditFavoritesMode.set(!this.isInEditFavoritesMode());
  }

  async confirmEditFavoritesMode() {
    await this.removeFavoritesTiles();
    await this.removeFavoritesSearches();
    this.tilesToRemoveFromFavorites.set([]);
    this.searchesToRemoveFromFavorite.set([]);
    this.isInEditFavoritesMode.set(false);
  }

  async removeFavoritesTiles() {
    const tilesToRemove = this.tilesToRemoveFromFavorites();
    await this.favoriteStore.removeManyTilesFromFavorites(tilesToRemove);
  }

  async removeFavoritesSearches() {
    const searchesToRemove = this.searchesToRemoveFromFavorite();
    await this.favoriteStore.removeManySearchesFromFavorites(searchesToRemove);
  }

  updateTilesToRemoveFromFavorites(tileId: string) {
    if (this.tilesToRemoveFromFavorites().includes(tileId)) {
      this.tilesToRemoveFromFavorites.set(
        this.tilesToRemoveFromFavorites().filter((t) => t !== tileId)
      );
    } else {
      this.tilesToRemoveFromFavorites.set([
        ...this.tilesToRemoveFromFavorites(),
        tileId,
      ]);
    }
  }

  updateSearchesToRemoveFromFavorite(searchId: number) {
    if (this.searchesToRemoveFromFavorite().includes(searchId)) {
      this.searchesToRemoveFromFavorite.set(
        this.searchesToRemoveFromFavorite().filter((s) => s !== searchId)
      );
    } else {
      this.searchesToRemoveFromFavorite.set([
        ...this.searchesToRemoveFromFavorite(),
        searchId,
      ]);
    }
  }

  async onFavoriteSearchClick(
    search: FavoriteFilterSearchInterface
  ): Promise<void> {
    await this.favoriteSearchApplier.applyFavoriteSearch(search);
  }
}
